import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { CalendarEvent, CalendarEventCreate, CalendarEventUpdate, CalendarFilters, ProximoEvento } from "@cold-crew/shared";
import type { PaginatedResponse } from "@cold-crew/shared";

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const calendarKeys = {
  all:      () => ["calendario"]                           as const,
  lists:    () => [...calendarKeys.all(), "list"]          as const,
  list:     (f: Partial<CalendarFilters>) =>
              [...calendarKeys.lists(), f]                 as const,
  detail:   (id: number) =>
              [...calendarKeys.all(), "detail", id]        as const,
  proximos: () => [...calendarKeys.all(), "proximos"]      as const,
};

// ─── List ─────────────────────────────────────────────────────────────────────

export function useEventos(filters: Partial<CalendarFilters> = {}) {
  return useQuery({
    queryKey: calendarKeys.list(filters),
    queryFn:  () =>
      api.get<PaginatedResponse<CalendarEvent>>("/calendario", filters as Record<string, any>),
  });
}

// ─── Single ───────────────────────────────────────────────────────────────────

export function useEvento(id: number) {
  return useQuery({
    queryKey: calendarKeys.detail(id),
    queryFn:  () => api.get<CalendarEvent>(`/calendario/${id}`),
    enabled:  !!id,
  });
}

// ─── Próximos (for Dashboard) ─────────────────────────────────────────────────

export function useProximosEventos(limit = 7) {
  return useQuery({
    queryKey: calendarKeys.proximos(),
    queryFn:  () =>
      api.get<{ data: ProximoEvento[]; total: number }>("/calendario/proximos", { limit }),
    staleTime: 5 * 60 * 1000, // 5 min
  });
}

// ─── Create ───────────────────────────────────────────────────────────────────

export function useCreateEvento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CalendarEventCreate) =>
      api.post<CalendarEvent>("/calendario", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: calendarKeys.all() }),
  });
}

// ─── Update ───────────────────────────────────────────────────────────────────

export function useUpdateEvento(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CalendarEventUpdate) =>
      api.put<CalendarEvent>(`/calendario/${id}`, data),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: calendarKeys.lists() });
      qc.invalidateQueries({ queryKey: calendarKeys.proximos() });
      qc.setQueryData(calendarKeys.detail(id), updated);
    },
  });
}

// ─── Delete ───────────────────────────────────────────────────────────────────

export function useDeleteEvento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/calendario/${id}`),
    onSuccess:  () => qc.invalidateQueries({ queryKey: calendarKeys.all() }),
  });
}

// ─── Restore ─────────────────────────────────────────────────────────────────

export function useRestoreEvento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<CalendarEvent>(`/calendario/${id}/restore`),
    onSuccess:  () => qc.invalidateQueries({ queryKey: calendarKeys.all() }),
  });
}
